import "bootstrap-icons/font/bootstrap-icons.css";
import "@vscode/codicons/dist/codicon.css";
import "./icons.css";

import { nullProtoRecord } from "@tsmono/util";

/**
 * Every icon the apps and shared packages render, keyed by meaning. Values
 * are Bootstrap Icons classes, except the search toggles (Codicons, to match
 * VS Code's find widget) and the `ii` classes (our own SVGs, see icons.css).
 */
export const iconClasses = {
  activity: "bi bi-activity",
  add: "bi bi-plus-lg",
  agent: "bi bi-grid",
  approvalApprove: "bi bi-shield-check",
  approvalEscalate: "bi bi-box-arrow-up",
  approvalModify: "bi bi-pencil-square",
  approvalReject: "bi bi-shield-x",
  approvalTerminate: "bi bi-shield-exclamation",
  approve: "bi bi-shield",
  arrowDown: "bi bi-arrow-down",
  arrowDownUp: "bi bi-arrow-down-up",
  arrowRight: "bi bi-arrow-right",
  arrowUp: "bi bi-arrow-up",
  cancel: "bi bi-x-circle",
  changeAdd: "bi bi-plus",
  changeRemove: "bi bi-dash",
  checkboxChecked: "bi bi-check-circle",
  checkboxUnchecked: "bi bi-circle",
  checkpoint: "bi bi-bookmark-check-fill",
  chevronDown: "bi bi-chevron-down",
  chevronLeft: "bi bi-chevron-left",
  chevronRight: "bi bi-chevron-right",
  chevronUp: "bi bi-chevron-up",
  clearText: "bi bi-x-circle-fill",
  close: "bi bi-x",
  closeLarge: "bi bi-x-lg",
  code: "bi bi-code-slash",
  codeSquare: "bi bi-code-square",
  collapseAll: "bi bi-arrows-collapse",
  columns: "bi bi-layout-three-columns",
  compaction: "bi bi-arrows-collapse-vertical",
  computer: "bi bi-display",
  config: "bi bi-gear",
  confirm: "bi bi-check",
  copy: "bi bi-copy",
  display: "bi bi-card-text",
  download: "bi bi-download",
  edit: "bi bi-pencil-square",
  error: "bi bi-exclamation-circle-fill",
  exclamation: "bi bi-exclamation",
  expandAll: "bi bi-arrows-expand",
  file: "bi bi-file-code",
  fileImage: "bi bi-file-image",
  filePdf: "bi bi-file-pdf",
  fileText: "bi bi-file-earmark-text",
  fileUnknown: "bi bi-file-earmark",
  filter: "bi bi-funnel",
  flag: "bi bi-flag-fill",
  flow: "ii inspect-flow",
  focusEnter: "bi bi-arrows-angle-expand",
  focusExit: "bi bi-arrows-angle-contract",
  fullscreen: "bi bi-arrows-fullscreen",
  fullscreenExit: "bi bi-fullscreen-exit",
  folder: "bi bi-folder",
  fork: "bi bi-sign-intersection-y-fill",
  globe: "bi bi-globe2",
  graph: "bi bi-graph-up",
  gridView: "bi bi-grid-3x3-gap",
  help: "bi bi-question-circle",
  highlight: "bi bi-highlighter",
  history: "bi bi-clock-history",
  info: "bi bi-info-circle",
  input: "bi bi-terminal",
  inspectFile: "ii inspect-icon-file",
  interrupt: "bi bi-slash-circle",
  lightbulb: "bi bi-lightbulb",
  limitCost: "bi bi-currency-dollar",
  limitCustom: "bi bi-exclamation-triangle",
  limitExecution: "bi bi-stopwatch",
  limitMessages: "bi bi-chat-right-text",
  limitOperator: "bi bi-person-workspace",
  limitTime: "bi bi-clock",
  limitTokens: "bi bi-list",
  limitTurns: "bi bi-arrow-repeat",
  link: "bi bi-link-45deg",
  listCheck: "bi bi-list-check",
  listView: "bi bi-list-ul",
  loading: "bi bi-arrow-clockwise",
  logCritical: "bi bi-fire",
  logDebug: "bi bi-bug",
  logError: "bi bi-x-circle",
  logHttp: "bi bi-download",
  logInfo: "bi bi-info-square",
  logNotset: "bi bi-card-text",
  logWarning: "bi bi-exclamation-triangle",
  matchCase: "codicon codicon-case-sensitive",
  matchRegex: "codicon codicon-regex",
  matchWholeWord: "codicon codicon-whole-word",
  metrics: "bi bi-clipboard-data",
  model: "bi bi-grid-3x3-gap",
  move: "bi bi-box-arrow-right",
  navBack: "ii inspect-icon-back",
  navForward: "ii inspect-icon-forward",
  navHome: "ii inspect-icon-home",
  navTasks: "ii inspect-icon-tasks",
  next: "bi bi-chevron-right",
  noSamples: "bi bi-ban",
  options: "bi bi-gear",
  pause: "bi bi-pause-fill",
  pencil: "bi bi-pencil",
  pendingTask: "bi bi-clock-fill",
  pendingTaskSubtle: "bi bi-clock",
  people: "bi bi-people",
  play: "bi bi-play-fill",
  playCircle: "bi bi-play-circle-fill",
  plug: "bi bi-plug",
  previous: "bi bi-chevron-left",
  print: "bi bi-printer",
  refresh: "bi bi-arrow-clockwise",
  refuse: "bi bi-ban",
  retry: "bi bi-arrow-repeat",
  running: "bi bi-stars",
  sample: "bi bi-database",
  samples: "bi bi-file-spreadsheet",
  sandbox: "bi bi-box-seam",
  scanner: "bi bi-radar",
  scorer: "bi bi-calculator",
  search: "bi bi-search",
  selectionChecked: "bi bi-check-lg",
  selectionClear: "bi bi-x-lg",
  selectionSelect: "bi bi-square",
  selectionSelecting: "bi bi-check2-square",
  sentinel: "bi bi-binoculars",
  sentinelFill: "bi bi-binoculars-fill",
  sidebar: "bi bi-list",
  signpost: "bi bi-signpost-split",
  solverDefault: "bi bi-arrow-return-right",
  solverUseTools: "bi bi-tools",
  sortDown: "bi bi-sort-down",
  sortUp: "bi bi-sort-up",
  success: "bi bi-check-circle-fill",
  successSubtle: "bi bi-check-circle",
  terminal: "bi bi-terminal",
  themeDark: "bi bi-moon-stars-fill",
  themeLight: "bi bi-sun-fill",
  threeDots: "bi bi-three-dots",
  toggleOff: "bi bi-toggle2-off",
  toggleOn: "bi bi-toggle-on",
  tools: "bi bi-tools",
  transcript: "bi bi-list-columns-reverse",
  trash: "bi bi-trash",
  treeClosed: "bi bi-caret-right-fill",
  treeOpen: "bi bi-caret-down-fill",
  validation: "bi bi-patch-check",
  warning: "bi bi-exclamation-triangle",
  window: "bi bi-window",
  wrap: "bi bi-text-wrap",
} as const;

export type IconName = keyof typeof iconClasses;

const kLogLevelIcons = nullProtoRecord(
  new Map<string, IconName>([
    ["notset", "logNotset"],
    ["debug", "logDebug"],
    ["http", "logHttp"],
    ["info", "logInfo"],
    ["warning", "logWarning"],
    ["error", "logError"],
    ["critical", "logCritical"],
  ])
);

/** Icon for a logger level; `undefined` for a level we don't recognise. */
export const logLevelIcon = (level: string): IconName | undefined =>
  kLogLevelIcons[level.toLowerCase()];

export const iconForMimeType = (mimeType: string): IconName => {
  if (mimeType === "application/pdf") {
    return "filePdf";
  } else if (mimeType.startsWith("image/")) {
    return "fileImage";
  } else {
    return "fileUnknown";
  }
};
